const selectorBase = "uic-menu";

/**
 * Find the menu-item that is closest to an event target.
 * @param {Event} event
 * @return {HTMLUicMenuItemElement|null}
 */
export function findMenuItemInEvent(
    event: Event
): HTMLUicMenuItemElement | null {
    return (event.target as HTMLElement).closest<HTMLUicMenuItemElement>(
        `${selectorBase}-item`
    );
}

/**
 * Find the first menu inside of an element.
 * @param {HTMLElement} el
 * @param {string} slug
 * @return {HTMLUicMenuElement|null}
 */
export function findMenuElement(
    el: HTMLElement,
    name?: string
): HTMLUicMenuElement {
    if (el === null) return null;
    return typeof name === "string"
        ? el.querySelector<HTMLUicMenuElement>(
              `${selectorBase}[name="${name}"]`
          )
        : el.querySelector<HTMLUicMenuElement>(`${selectorBase}`);
}

/**
 * Find all menus inside of an element.
 * @param {HTMLElement} el
 * @param {string} name
 * @return {HTMLUicMenuElement[]}
 */
export function findMenuElements(
    el: HTMLElement,
    name?: string
): HTMLUicMenuElement[] {
    if (el === null) return [];
    const menuNodes =
        typeof name === "string"
            ? el.querySelectorAll<HTMLUicMenuElement>(
                  `${selectorBase}[name="${name}"]`
              )
            : el.querySelectorAll<HTMLUicMenuElement>(`${selectorBase}`);
    return [...menuNodes];
}

/**
 * Find all menu-items inside of an element.
 * @param {HTMLElement} el
 * @param {string} selector
 * @return {HTMLUicMenuItemElement[]}
 */
export function findMenuItemElements(
    el: HTMLElement,
    selector: string = ""
): HTMLUicMenuItemElement[] {
    if (el === null) return [];
    const itemSelector = `${selectorBase}-item${selector}`;
    return [...el.querySelectorAll<HTMLUicMenuItemElement>(itemSelector)];
}

/**
 * Find first menu-item that is selected.
 * @param {HTMLElement} el
 * @return {HTMLUicMenuItemElement|null}
 */
export function findSelectedMenuItemElement(
    el: HTMLElement
): HTMLUicMenuItemElement {
    if (el === null) return null;
    return el.querySelector<HTMLUicMenuItemElement>(
        `${selectorBase}-item[selected]`
    );
}

/**
 * Find a menu-item with a specific slug.
 * @param {HTMLElement} el
 * @param {string} slug
 * @return {HTMLUicMenuItemElement|null}
 */
export function findMenuItemWithSlug(
    el: HTMLElement,
    slug: string
): HTMLUicMenuItemElement {
    if (el === null || slug === null) {
        return null;
    }
    return el.querySelector<HTMLUicMenuItemElement>(
        `${selectorBase}-item[slug="${slug}"]`
    );
}

/**
 * Returns the root element in a nested menu.
 * @param {HTMLUicMenuItemElement} el
 * @return {HTMLUicMenuItemElement}
 */
export function getMenuItemRoot(
    el: HTMLUicMenuItemElement
): HTMLUicMenuItemElement | null {
    if (el !== null && el.parent !== null) {
        return getMenuItemRoot(el.parent);
    }
    return el;
}

/**
 * Returns the root element in a nested menu.
 * @param {HTMLUicMenuItemElement} el
 * @param {string[]} path
 * @return {string[]}
 */
export function getMenuItemPathReverse(
    el: HTMLUicMenuItemElement,
    path: string[] = []
): string[] {
    if (path === null) path = [];
    if (el === null) return path;
    path.push(el.slug);
    if (el.parent !== null) {
        getMenuItemPathReverse(el.parent, path);
    }
    return path;
}

/**
 * Returns the root element in a nested menu.
 * @param {HTMLUicMenuItemElement} el
 * @return {string[]}
 */
export function getMenuItemPath(el: HTMLUicMenuItemElement): string[] {
    return getMenuItemPathReverse(el).reverse();
}

/**
 * Calculates the summed wdth of all menu-items inside of an element.
 * @param {HTMLElement} el
 * @return {number}
 */
export function calculateMenuItemsTotalWidth(el: HTMLElement): number {
    let menuWidth: number = 0;
    findMenuItemElements(el).forEach((el: HTMLElement) => {
        menuWidth += el.offsetWidth;
    });
    return menuWidth;
}

/**
 * Calculates the summed wdth of all menu-items inside of an element.
 * @param {HTMLElement} el
 * @param {string} slug
 * @return {number}
 */
export function calculateMenuItemOffsetLeft(
    el: HTMLElement,
    slug: string
): number {
    let offsetX: number = 0;
    let found: boolean = false;
    findMenuItemElements(el).forEach((item: HTMLUicMenuItemElement) => {
        if (!found) {
            if (item.slug === slug) {
                found = true;
            } else {
                offsetX += item.offsetWidth;
            }
        }
    });
    return found ? offsetX : 0;
}

/**
 * Extracts the label text of a menu-item.
 * @param {HTMLUicMenuItemElement} menuItem
 * @return {string}
 */
export function getMenuItemLabel(menuItem: HTMLUicMenuItemElement): string {
    if (menuItem === null) return "";
    if (typeof menuItem.label === "string") {
        return menuItem.label;
    }
    const slot = menuItem.querySelector<HTMLElement>(
        `.${selectorBase}-item__label-slot`
    );
    if (slot === null) return "";
    return slot.textContent || slot.innerText || menuItem.slug || "";
}

/**
 * Returns `true` if the item has child items.
 * @param {HTMLUicMenuItemElement} menuItem
 * @return {boolean}
 */
export function getMenuItemHasChildren(
    menuItem: HTMLUicMenuItemElement
): boolean {
    return (
        menuItem !== null &&
        Array.isArray(menuItem.items) &&
        menuItem.items.length > 0
    );
}

/**
 * Returns `true` if the item is the first child item
 * of menu item with children.
 * @param {HTMLUicMenuItemElement} menuItem
 * @return {boolean}
 */
export function getIsFirstChildMenuItem(
    menuItem: HTMLUicMenuItemElement
): boolean {
    return (
        menuItem.parent &&
        getMenuItemHasChildren(menuItem.parent) &&
        menuItem.parent.items[0].slug === menuItem.slug
    );
}

/**
 * Find the next menu item in the expanded tree
 * @param {HTMLUicMenuItemElement} item
 * @param {HTMLElement} scope
 * @return {HTMLUicMenuItemElement | null}
 */
export function findNextItemRecursive(
    item: HTMLUicMenuItemElement,
    scope: HTMLElement = null
): HTMLUicMenuItemElement | null {
    const parentItem = item.parent || null;
    if (parentItem === null && scope === null) {
        return null;
    }
    const items = findMenuItemElements(
        parentItem || scope,
        `[level="${item.level}"]`
    );
    const index = items.indexOf(item);
    if (index === items.length - 1) {
        return parentItem !== null
            ? findNextItemRecursive(parentItem, scope)
            : null;
    }
    return items[index + 1];
}

/**
 * Find the previous menu item in the expanded tree
 * @param {HTMLUicMenuItemElement} item
 * @param {HTMLElement} scope
 * @return {HTMLUicMenuItemElement | null}
 */
export function findPrevItemRecursive(
    item: HTMLUicMenuItemElement,
    scope: HTMLElement = null
): HTMLUicMenuItemElement | null {
    const parentItem = item.parent || null;
    if (parentItem === null && scope === null) {
        return null;
    }
    const items = findMenuItemElements(
        parentItem || scope,
        `[level="${item.level}"]`
    );
    const index = items.indexOf(item);
    if (index === 0) {
        return parentItem;
    }
    const prevItem = items[index - 1] || null;
    if (prevItem === null) {
        return null;
    }
    return prevItem.expanded ? findLastChildItemRecursive(prevItem) : prevItem;
}

/**
 * Find the last menu item in the expanded tree
 * @param {HTMLUicMenuItemElement} item
 * @return {HTMLUicMenuItemElement | null}
 */
export function findLastChildItemRecursive(
    item: HTMLUicMenuItemElement
): HTMLUicMenuItemElement | null {
    const items = findMenuItemElements(item, `[level="${item.level + 1}"]`);
    const lastItem = items[items.length - 1] || null;
    if (lastItem === null) {
        return null;
    }
    return lastItem.expanded ? findLastChildItemRecursive(lastItem) : lastItem;
}
